import React from "react";
import styles from "./ShippingFeeDialog.module.scss";
import { Dialog, DialogTitle, Button, Radio, RadioGroup, FormControlLabel, FormLabel, DialogContent } from "@mui/material";
import { ShippingCo, ShippingCoType, ShippingType } from "../../../libs/constants";
import { ShippingFeeDialogItem } from "../../../libs/types";
import CloseIcon from "@mui/icons-material/Close";
import InputGroup from "../../core/InputGroup";
import DateSelect from "../../core/DateSelect";
import { BrandlistResponseData } from "../../../apis/models/BrandlistResponse";
import { InvoiceMasterData } from "../../../apis/models/InvoiceMasterRequest";
import { format } from "date-fns";
import DateWithoutTime from "../../../libs/dateWithoutTime";


const ShippingFeeDialog: React.FC<ShippingFeeModalProps> = (props: ShippingFeeModalProps) => {
  const { targetBrand, targetInvoice, onClose, onSave, open, existDates } = props;
  const [shippingCompany, setShippingCompany] = React.useState<ShippingCo.yamato | ShippingCo.sagawa>(ShippingCo.sagawa);
  const [shippingType, setShippingType] = React.useState<ShippingType.COD | ShippingType.PIA>(ShippingType.PIA);
  const initialItem = {
    actualShippingFee: 0,
    dateAdded: DateWithoutTime.now(),
    quantity: 0,
    referenceNo: "",
    memo: "",
    unitPrice: 0,
    managementNo: "",
    postcode: "",
  }
  const [data, setData] = React.useState<ShippingFeeDialogItem[]>([initialItem]);
  const [isWarning, setIsWarning] = React.useState<boolean>(false);
  const [changePackage, setChangePackage] = React.useState({isChangingCo: false, val: ""});
  const [saveBtnIsClicked, setSaveBtnIsClicked] = React.useState<boolean>(false);

  const changeForm = (isChangingCo: boolean, val: string) => {
    setIsWarning(true);
    setChangePackage({isChangingCo, val})
  }
  
  const confirmChange = () => {
    if (changePackage.isChangingCo) {
      setShippingCompany(changePackage.val === ShippingCo.sagawa ? ShippingCo.sagawa : ShippingCo.yamato);
    } else {
      setShippingType(changePackage.val === ShippingType.COD ? ShippingType.COD : ShippingType.PIA)
    }
    setIsWarning(false)
    setSaveBtnIsClicked(false);
    setData([{...initialItem}])
  }

  const handleEdit = (id: number, newItem: ShippingFeeDialogItem) => {
    const copyData = [...data];
    copyData.splice(id, 1, newItem);
    setData(copyData);
  }

  const handleDelete = (id: number) => {
    if (data.length <= 1) {
      return
    }
    const copyData = [...data];
    copyData.splice(id, 1);
    setData(copyData);
  }

  const validCheck = () => {
    if (data.some(item => item.quantity <= 0 || item.unitPrice <= 0 || item.actualShippingFee <= 0 || item.dateAdded === undefined)) {
      setSaveBtnIsClicked(true);
      data.some(item => item.dateAdded !== undefined ? (item.dateAdded) : undefined)
    } else {
      onSave(data, shippingCompany+shippingType === ShippingCoType.yCOD ? ShippingCoType.yCOD : shippingCompany+shippingType === ShippingCoType.yPIA ? ShippingCoType.yPIA : shippingCompany+shippingType === ShippingCoType.sCOD ? ShippingCoType.sCOD : ShippingCoType.sPIA)
    }
  }

  const getInvoiceAtDate = (value: string) => {
    if (!targetInvoice) {
      return undefined
    }
    const invoiceFrom = format(new Date(targetInvoice.from), "yyyy/MM/dd")
    const invoiceTo = format(new Date(targetInvoice.to), "yyyy/MM/dd")
    return (invoiceFrom <= value  && value <= invoiceTo)
  }

  const validateDateAdded = (value: DateWithoutTime): string | undefined => {
    const dateString = value.getValue4Display()
    if (value.getValue4Display().length === 0) {
      return "入力必須です"
    } else if (value.isInvalid()) {
      return "年月日で入力してください"
    } 
    else if (existDates.some(d => d.equals(value))) {
      return "すでに入力されている日付です"
    } 
    else if (!targetInvoice && !getInvoiceAtDate(dateString)) {
      return "請求期間の範囲で入力してください"
    } else if (targetInvoice) {
      const invoiceFrom = format(new Date(targetInvoice.from), "yyyy/MM/dd")
      const invoiceTo = format(new Date(targetInvoice.to), "yyyy/MM/dd")
      if (dateString < invoiceFrom || invoiceTo < dateString) {
        return "請求期間の範囲で入力してください"
      }
    }
    return undefined
  }

  const invoicePeriod = React.useMemo(() => {
    if (targetInvoice){
      return `${format(new Date(targetInvoice.from), "yyyy/MM/dd")} ～ ${format(new Date(targetInvoice.to), "yyyy/MM/dd")}`
    }
    return ''
  }, [targetInvoice])

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="lg" PaperProps={{sx: {height: '90%'}}} >
      <DialogTitle sx={{ fontWeight: 700, fontSize: "24px", paddingLeft: "40px"}}>{targetBrand?.brandName ?? ''} - 運賃費追加</DialogTitle>
      <DialogContent sx={{padding: "0 40px 24px", display: "flex", flexDirection: "column"}}>
        <div>
          <div className={styles.dialog__label}>
            <span>請求期間：</span>
            <span className={styles.dialog__label_content}>{invoicePeriod}</span>
          </div>
          <div className={styles.radio_group}>
            <div>
              <FormLabel required className={styles.required} id="company-selection" sx={{ fontSize: "14px", ".MuiFormLabel-asterisk": {color: "#EB4E4E"}}}>運送会社</FormLabel>
              <RadioGroup row value={shippingCompany} onChange={(e) => changeForm(true, e.target.value)}>
                <FormControlLabel value={ShippingCo.sagawa} style={{height: "30px", marginTop: "-2px"}} control={<Radio/>} label="佐川急便" sx={{ fontSize: "16px" }}/>
                <FormControlLabel value={ShippingCo.yamato} style={{height: "30px", marginTop: "-2px"}} control={<Radio />} label="ヤマト" sx={{ fontSize: "16px" }} />
              </RadioGroup>
            </div>
            <div className={styles.marginLeft50}>
              <FormLabel required className={styles.required} id="payment-method-selection" sx={{ fontSize: "14px", ".MuiFormLabel-asterisk": {color: "#EB4E4E"}}}>発送方法</FormLabel>
              <RadioGroup row value={shippingType} onChange={(e) => changeForm(false, e.target.value)}>
                <FormControlLabel value={ShippingType.PIA} style={{height: "30px", marginTop: "-2px"}} control={<Radio />} label="元払い"/>
                <FormControlLabel value={ShippingType.COD} style={{height: "30px", marginTop: "-2px"}} control={<Radio />} label="着払い" sx={{ fontSize: "16px" }}/>
              </RadioGroup>
            </div>
          </div>
        </div>
        <div className={styles.table_wrapper}>
          {
            isWarning ?
            <div className={styles.warning}>
              <div className={styles.warning__text}>
                こちらの運送会社・発送方法を変更すると、入力された内容が消えますが、<br/>
                変更してよろしいでしょうか？
              </div>
              <div className={styles.warning__functions}>
                <Button variant="outlined" color="primary" onClick={() => {setChangePackage({isChangingCo: false, val: ""}); setIsWarning(false)}}>キャンセル</Button>
                <Button variant="contained" color="primary" onClick={() => confirmChange()} disableElevation>変更</Button>
              </div>
            </div> : <></>
          }
          {
            shippingCompany === ShippingCo.sagawa && shippingType === ShippingType.PIA ? 
            data.map((item, index) => (
              <div key={index} className={styles.rowStyle}>
                <div className={styles.longtable}>
                  <InputGroup label="管理番号" type="text" onChange={(val) => handleEdit(index, {...item, managementNo: val})} value={item.managementNo && item.managementNo !== undefined ? item.managementNo : ""}/>
                  <InputGroup label="郵便番号" type="text" onChange={(val) => handleEdit(index, {...item, postcode: val})} value={item.postcode && item.postcode !== undefined ? item.postcode : ""}/>
                  <InputGroup label="問い合わせNo." type="text" onChange={(val) => handleEdit(index, {...item, referenceNo: val})} value={item.referenceNo && item.referenceNo !== undefined ? item.referenceNo : ""}/>
                  <DateSelect handleDateChange={(date) => handleEdit(index, {...item, dateAdded: date ? new DateWithoutTime(date) : undefined})} selectedDate={item.dateAdded !== undefined ? item.dateAdded?.getDate() : null} label="日付" required errMess={saveBtnIsClicked && item.dateAdded !== undefined ? validateDateAdded(item.dateAdded) : undefined}/>
                  <InputGroup label="運賃単価" required type="number" onChange={(val) => handleEdit(index, {...item, quantity: val})} value={item.quantity === 0 ? "" : item.quantity} error={saveBtnIsClicked && item.quantity <= 0}/>
                  <InputGroup label="個数" required type="number" onChange={(val) => handleEdit(index, {...item, unitPrice: val})} value={item.unitPrice === 0 ? "" : item.unitPrice} error={saveBtnIsClicked && item.unitPrice <= 0}/>
                  <InputGroup label="実運賃" required type="number" onChange={(val) => handleEdit(index, {...item, actualShippingFee: val})} value={item.actualShippingFee === 0 ? "" : item.actualShippingFee} error={saveBtnIsClicked && item.actualShippingFee <= 0}/>
                  <InputGroup label="備考" type="text" onChange={(val) => handleEdit(index, {...item, memo: val})} value={item.memo && item.memo !== undefined ? item.memo : ""}/>
                </div>
                <div className={styles.close_holder}><CloseIcon color="primary" className={styles.delete_icon} onClick={() => handleDelete(index)}/></div>
              </div>
            )) : 
            data.map((item, index) => (
              <div key={index} className={styles.rowStyle}>
                <div className={styles.shorttable}>
                  <InputGroup label="問い合わせNo." type="text" onChange={(val) => handleEdit(index, {...item, referenceNo: val})} value={item.referenceNo && item.referenceNo !== undefined ? item.referenceNo : ""}/>
                  <DateSelect handleDateChange={(date) => handleEdit(index, {...item, dateAdded: date ? new DateWithoutTime(date) : undefined})} selectedDate={item.dateAdded !== undefined ? item.dateAdded?.getDate() : null} label="日付" required errMess={saveBtnIsClicked && item.dateAdded !== undefined ? validateDateAdded(item.dateAdded) : undefined}/>
                  <InputGroup label="運賃単価" required type="number" onChange={(val) => handleEdit(index, {...item, quantity: val})} value={item.quantity === 0 ? "" : item.quantity} error={saveBtnIsClicked && item.quantity <= 0}/>
                  <InputGroup label="個数" required type="number" onChange={(val) => handleEdit(index, {...item, unitPrice: val})} value={item.unitPrice === 0 ? "" : item.unitPrice} error={saveBtnIsClicked && item.unitPrice <= 0}/>
                  <InputGroup label="実運賃" required type="number" onChange={(val) => handleEdit(index, {...item, actualShippingFee: val})} value={item.actualShippingFee === 0 ? "" : item.actualShippingFee} error={saveBtnIsClicked && item.actualShippingFee <= 0}/>
                  <InputGroup label="備考" type="text" onChange={(val) => handleEdit(index, {...item, memo: val})} value={item.memo && item.memo !== undefined ? item.memo : ""}/>
                </div>
                <div className={styles.close_holder}><CloseIcon color="primary" className={styles.delete_icon} onClick={() => handleDelete(index)}/></div>
              </div>
            ))}
        </div>
        <div className={styles.dialog__add}>
              <Button variant="outlined" color="primary" onClick={() => setData([...data, initialItem])}>＋行追加</Button>
          </div>
          <div className={styles.dialog__functions}>
              <Button variant="outlined" color="primary" onClick={() => {setData([initialItem]); onClose()}}>キャンセル</Button>
              <Button variant="contained" color="primary" onClick={() => validCheck()}>登録</Button>
          </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShippingFeeDialog;

export type ShippingFeeModalProps = {
  targetBrand?: BrandlistResponseData;
  targetInvoice?: InvoiceMasterData;
  open: boolean;
  existDates: DateWithoutTime[];
  onClose: () => void;
  onSave: (val: ShippingFeeDialogItem[], type: ShippingCoType.yCOD | ShippingCoType.yPIA | ShippingCoType.sCOD | ShippingCoType.sPIA) => void;
};
